import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { inventoryNewService } from '../services/inventoryNew.service';
import type { InventoryCreatePayload, InventoryNewFormValues } from '../types/inventoryNew.types';

const defaultValues: InventoryNewFormValues = {
  code: '',
  name: '',
  category_name: '',
  unit: '',
  specification: '',
  item_type: 'main',
  supplier_name: '',
  origin_name: '',
  manufacturer_code: '',
  unit_price: 0,
  opening_stock: 0,
  minimum_stock: 0,
  warehouse_id: '',
  storage_location: '',
  note: '',
  is_stock_tracked: true,
  height: '',
  canopy_diameter: '',
  pot_spec: '',
  substrate: '',
  water_requirement: '',
  light_requirement: '',
  seasonality: '',
  expiry_date: '',
};

export function useInventoryNew() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [values, setValues] = useState<InventoryNewFormValues>(defaultValues);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const lookupsQuery = useQuery({
    queryKey: ['inventory', 'new', 'lookups', companyId],
    queryFn: () => inventoryNewService.getLookups(companyId),
    enabled: !!companyId,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ keepForm }: { keepForm: boolean }) => {
      let thumbnailUrl: string | null = null;
      if (imageFile && companyId) {
        thumbnailUrl = await inventoryNewService.uploadImage(imageFile, companyId);
      }

      const userId = profile?.id || '';
      const payload: InventoryCreatePayload = {
        ...values,
        company_id: companyId,
        code: values.code.trim(),
        thumbnail_url: thumbnailUrl,
        // Wrap extended specs into specifications JSONB
        specifications: {
          height: values.height,
          canopy_diameter: values.canopy_diameter,
          pot_spec: values.pot_spec,
          substrate: values.substrate,
          water_requirement: values.water_requirement,
          light_requirement: values.light_requirement,
          seasonality: values.seasonality,
          expiry_date: values.expiry_date,
        }
      } as any;
      const id = await inventoryNewService.createInventoryItem(payload, userId, companyId);
      if (keepForm) {
        setValues(defaultValues);
        setImageFile(null);
        setImagePreview('');
      }
      return id;
    },
  });

  const stockValue = useMemo(() => values.unit_price * values.opening_stock, [values.unit_price, values.opening_stock]);

  return {
    values,
    setValues,
    imageFile,
    setImageFile,
    imagePreview,
    setImagePreview,
    stockValue,
    companyId,
    lookups: lookupsQuery.data,
    isLoadingLookups: lookupsQuery.isLoading,
    createItem: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
}
